function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useClassPrepend } from "../../../index";
import useSelect, { SelectProvider } from '@mui/base/useSelect';
import Option from '@mui/base/Option';

// Props:
// - classPrepend: (string) CSS class name prefix to apply custom styling.
// - options: (array) An array of options for the select.
// - name: (string) The name attribute of the select.
// - placeholder: (string,optional) The placeholder text to display when no option is selected.
// - helperText: (string) Additional helper text to display.
// - required: (boolean) When true, the select is required.
// - label: (string, optional) The label for the select.
// - multiple: (boolean, optional) When true multiple options can be selected, which turns the select's value into an array of selected options
// - ...rest: (props) Additional props to be passed to the underlying select component.

function UnstyledSelect({
  classPrepend = "unstyled",
  className = "",
  options,
  name = "",
  placeholder = "",
  helperText = "",
  required = false,
  label = "",
  toggleIcon = "▾",
  showToggleIcon = true,
  multiple = false,
  disabled = false,
  ...rest
}) {
  const [getClassName, getClassNameWithGlobal, getClassNameValue] = useClassPrepend(classPrepend);
  const [listboxVisible, setListboxVisible] = useState(false);
  const listboxRef = useRef(null);
  const {
    getButtonProps,
    getListboxProps,
    contextValue,
    value
  } = useSelect({
    listboxRef,
    open: listboxVisible,
    onOpenChange: open => setListboxVisible(open),
    multiple: multiple,
    disabled: disabled,
    ...rest
  });
  const selectedOption = useMemo(() => {
    // if multi select is enabled, then return a comma separated list of all the selected options names
    if (multiple === true) {
      if (value.length === 0) return null;
      const selectedOptions = options.filter(option => value.includes(option.value));
      const selectedOptionNames = selectedOptions.map(option => option.label);
      const selectionOptionDisplay = selectedOptionNames.join(", ");
      return selectionOptionDisplay;
    }

    // otherwise multi select is not enabled, so return the selected option name
    if (value === null) return null;
    return options.find(option => option.value === value).label;
  }, [value, options]);
  useEffect(() => {
    if (listboxVisible) {
      listboxRef.current?.focus();
    }
  }, [listboxVisible]);
  return /*#__PURE__*/React.createElement("div", {
    className: `${classPrepend}-select-container ${disabled ? classPrepend + "-select-container-disabled" : ""} ${getClassNameValue(className)}`
  }, label && name && /*#__PURE__*/React.createElement("label", {
    htmlFor: name,
    className: `${classPrepend}-select-label`
  }, label), /*#__PURE__*/React.createElement("div", {
    className: `${classPrepend}-select-wrapper ${getClassNameValue(className)}`
  }, /*#__PURE__*/React.createElement("button", _extends({
    name: name
  }, getButtonProps(), {
    className: `${classPrepend}-select ${listboxVisible ? 'expanded' : ''} ${getClassNameValue(className)}`
  }), selectedOption ? selectedOption : placeholder, showToggleIcon && /*#__PURE__*/React.createElement("span", {
    className: `${classPrepend}-after-content`
  }, toggleIcon)), /*#__PURE__*/React.createElement("ul", _extends({}, getListboxProps(), {
    className: `${classPrepend}-select-listbox ${listboxVisible ? 'visible' : 'hidden'}`
  }), /*#__PURE__*/React.createElement(SelectProvider, {
    value: contextValue
  }, options.map((option, index) => /*#__PURE__*/React.createElement(Option, {
    className: `${classPrepend}-select-option ${option?.disabled === true ? classPrepend + "-select-option-disabled" : ""}`,
    key: option.value,
    value: option.value,
    label: option.label,
    disabled: option?.disabled || false
  }, option.label)))), helperText && /*#__PURE__*/React.createElement("p", null, helperText)));
}
export { UnstyledSelect };